import React, { useState, useEffect } from 'react';
import Loading from '../components/Loading';
import { getMoonPhaseReading, isGeminiReady } from '../services/geminiService';
import { useLanguage } from '../context/LanguageContext';

// Moon phase calculation
function getMoonPhase(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  // Calculate Julian date
  let jd;
  if (month <= 2) {
    jd = Math.floor(365.25 * (year - 1)) + Math.floor(30.6001 * (month + 13)) + day + 1720995;
  } else {
    jd = Math.floor(365.25 * year) + Math.floor(30.6001 * (month + 1)) + day + 1720995;
  }

  const B = 2 - Math.floor(year / 100) + Math.floor(year / 400);
  jd = jd + B;

  // Calculate moon phase (0-29.53 days)
  const lunarCycle = 29.53058867;
  const knownNewMoon = 2451550.1; // Jan 6, 2000
  const daysSinceNew = jd - knownNewMoon;
  const currentCycle = daysSinceNew / lunarCycle;
  const moonAge = (currentCycle - Math.floor(currentCycle)) * lunarCycle;

  // Determine phase
  const phases = [
    { name: 'New Moon', emoji: '🌑', phaseName: 'newMoon', start: 0, end: 1.85 },
    { name: 'Waxing Crescent', emoji: '🌒', phaseName: 'waxingCrescent', start: 1.85, end: 5.53 },
    { name: 'First Quarter', emoji: '🌓', phaseName: 'firstQuarter', start: 5.53, end: 9.22 },
    { name: 'Waxing Gibbous', emoji: '🌔', phaseName: 'waxingGibbous', start: 9.22, end: 12.91 },
    { name: 'Full Moon', emoji: '🌕', phaseName: 'fullMoon', start: 12.91, end: 16.61 },
    { name: 'Waning Gibbous', emoji: '🌖', phaseName: 'waningGibbous', start: 16.61, end: 20.30 },
    { name: 'Last Quarter', emoji: '🌗', phaseName: 'lastQuarter', start: 20.30, end: 23.99 },
    { name: 'Waning Crescent', emoji: '🌘', phaseName: 'waningCrescent', start: 23.99, end: 29.53 },
  ];

  for (const phase of phases) {
    if (moonAge >= phase.start && moonAge < phase.end) {
      return { ...phase, age: moonAge, illumination: Math.round(getIllumination(moonAge) * 100) };
    }
  }
  return { ...phases[0], age: moonAge, illumination: Math.round(getIllumination(moonAge) * 100) };
}

function getIllumination(moonAge) {
  const phase = moonAge / 29.53058867;
  return 0.5 * (1 - Math.cos(2 * Math.PI * phase));
}

// Get phases for a month
function getMonthPhases(year, month) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const phases = [];
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    phases.push({
      date,
      day,
      ...getMoonPhase(date),
    });
  }
  return phases;
}

// Tithi calculation (approximate)
function getTithi(moonAge) {
  const tithiIndex = Math.floor(moonAge / (29.53058867 / 30));
  const tithis = [
    'Pratipada', 'Dwitiya', 'Tritiya', 'Chaturthi', 'Panchami',
    'Shashthi', 'Saptami', 'Ashtami', 'Navami', 'Dashami',
    'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Purnima/Amavasya'
  ];
  const paksha = moonAge < 14.77 ? 'Shukla' : 'Krishna';
  return {
    name: tithis[tithiIndex % 15],
    paksha,
    fullName: `${paksha} ${tithis[tithiIndex % 15]}`,
  };
}

export default function MoonPhase() {
  const { language, t } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMonth, setViewMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [monthPhases, setMonthPhases] = useState([]);
  const [reading, setReading] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const currentPhase = getMoonPhase(selectedDate);
  const currentTithi = getTithi(currentPhase.age);

  useEffect(() => {
    setMonthPhases(getMonthPhases(viewMonth.getFullYear(), viewMonth.getMonth()));
  }, [viewMonth]);

  const handleGetReading = async () => {
    if (!isGeminiReady()) {
      setError(t('apiKeyError'));
      return;
    }
    setLoading(true);
    setError('');
    setReading('');
    try {
      const result = await getMoonPhaseReading(
        currentPhase.name,
        currentTithi.fullName,
        selectedDate.toLocaleDateString(),
        language
      );
      setReading(result);
    } catch (err) {
      setError(err.message || 'Failed to get reading.');
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (delta) => {
    const newDate = new Date(viewMonth);
    newDate.setMonth(newDate.getMonth() + delta);
    setViewMonth(newDate);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isSelected = (date) => {
    return date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const firstDayOfMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1).getDay();

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <h2>{t('moonPhaseTitle')}</h2>
          <p>{t('moonPhaseDesc')}</p>
        </div>

        {/* Current Moon Display */}
        <div className="moon-display">
          <div className="moon-visual">
            <span className="moon-emoji">{currentPhase.emoji}</span>
            <div className="moon-info">
              <h3>{t(currentPhase.phaseName) || currentPhase.name}</h3>
              <p className="tithi">{currentTithi.fullName}</p>
              <p className="illumination">{currentPhase.illumination}% {t('illuminated')}</p>
              <p className="selected-date">
                {selectedDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Moon Calendar */}
        <div className="moon-calendar">
          <div className="calendar-header">
            <button onClick={() => changeMonth(-1)} className="btn btn-secondary">←</button>
            <h3>
              {viewMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h3>
            <button onClick={() => changeMonth(1)} className="btn btn-secondary">→</button>
          </div>

          <div className="calendar-weekdays">
            {weekDays.map(day => (
              <div key={day} className="weekday">{day}</div>
            ))}
          </div>

          <div className="calendar-grid">
            {/* Empty cells for days before first of month */}
            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={`empty-${i}`} className="calendar-day empty"></div>
            ))}

            {/* Days of the month */}
            {monthPhases.map((phase) => (
              <div
                key={phase.day}
                className={`calendar-day ${isToday(phase.date) ? 'today' : ''} ${isSelected(phase.date) ? 'selected' : ''}`}
                onClick={() => setSelectedDate(phase.date)}
              >
                <span className="day-number">{phase.day}</span>
                <span className="day-moon">{phase.emoji}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Auspicious Activities */}
        <div className="moon-activities">
          <h3>{t('moonActivitiesTitle')}</h3>
          <div className="activities-grid">
            <div className={`activity-card ${currentPhase.name.includes('Waxing') || currentPhase.name === 'Full Moon' ? 'good' : 'neutral'}`}>
              <span className="activity-icon">🌱</span>
              <span>{t('newBeginnings')}</span>
              <span className="activity-status">{currentPhase.name.includes('Waxing') ? '✓ ' + t('favorable') : t('waitForWaxing')}</span>
            </div>
            <div className={`activity-card ${currentPhase.name === 'Full Moon' ? 'good' : 'neutral'}`}>
              <span className="activity-icon">🧘</span>
              <span>{t('meditation')}</span>
              <span className="activity-status">{currentPhase.name === 'Full Moon' ? '✓ ' + t('ideal') : t('goodAnytime')}</span>
            </div>
            <div className={`activity-card ${currentPhase.name.includes('Waning') ? 'good' : 'neutral'}`}>
              <span className="activity-icon">🧹</span>
              <span>{t('cleansing')}</span>
              <span className="activity-status">{currentPhase.name.includes('Waning') ? '✓ ' + t('favorable') : t('waitForWaning')}</span>
            </div>
            <div className={`activity-card ${currentTithi.name === 'Ekadashi' ? 'good' : 'neutral'}`}>
              <span className="activity-icon">🍽️</span>
              <span>{t('fasting')}</span>
              <span className="activity-status">{currentTithi.name === 'Ekadashi' ? '✓ ' + t('ekadashiToday') : t('nextEkadashi')}</span>
            </div>
          </div>
        </div>

        {/* Get Detailed Reading */}
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button className="btn btn-primary" onClick={handleGetReading} disabled={loading}>
            {loading ? t('readingStars') : t('getMoonReading')}
          </button>
        </div>

        {loading && <Loading message={t('loadingMoonReading')} />}
        {error && <div className="error-message">{error}</div>}

        {reading && (
          <div className="result-container">
            <div className="result-card">
              <h3>🌙 {t('moonReadingResult')}</h3>
              <div className="content">{reading}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
